import axios from 'axios';

import type { CapturedLogEntry } from '../utils/console-capture';
import { BUG_TRACKER_AUTH_EXPIRED, exchangeBugTrackerToken } from './exchange-bug-tracker-token';

export type BugReportResponse = {
	id: string;
	title: string;
	status: string;
	reportedBy: string;
	createdAt: string;
};

export type BugReportScreenshot = {
	fileName?: string | null;
	contentType?: string | null;
	dataBase64: string;
};

export type SubmitBugReportParams = {
	description: string;
	url?: string | null;
	userAgent?: string | null;
	urgency?: string | null;
	system?: string | null;
	requestType?: string | null;
	otherLinks?: string | null;
	screenshots?: Array<BugReportScreenshot> | null;
	meta?: Record<string, unknown> | null;
	logs?: Array<CapturedLogEntry> | null;
	blueprint?: Record<string, unknown> | null;
	reporterRoles?: Array<string> | null;
	applicationId?: string;
	getToken: () => Promise<string>;
};

export const submitBugReport = async (params: SubmitBugReportParams): Promise<[BugReportResponse | null, string | null]> => {
	const { description, url, userAgent, urgency, system, requestType, otherLinks, screenshots, meta, logs, blueprint, reporterRoles, applicationId = 'blueprint', getToken } = params;

	try {
		const blueprintToken = await getToken();
		// blueprintToken is audienced for blueprint:api:dev -- exchange it for the bug-tracker's audience first.
		const [token, exchangeError] = await exchangeBugTrackerToken({ keystoneUrl: import.meta.env.VITE_KEYSTONE_URL, subjectToken: blueprintToken });
		if (!token) {
			return [null, exchangeError ?? 'Failed to authorize bug-tracker request'];
		}

		const response = await axios.post<BugReportResponse>(
			`${import.meta.env.VITE_BUG_API_URL}/bug-reports`,
			{
				description,
				url: url ?? null,
				userAgent: userAgent ?? null,
				urgency: urgency ?? null,
				system: system ?? null,
				requestType: requestType ?? null,
				otherLinks: otherLinks ?? null,
				screenshots: screenshots ?? null,
				meta: meta ?? null,
				logs: logs ?? null,
				blueprint: blueprint ?? null,
				reporterRoles: reporterRoles ?? null,
				applicationId,
				authorizationHeader: `Bearer ${token}`
			},
			{ headers: { Authorization: `Bearer ${token}` } }
		);
		return [response.data, null];
	} catch (error) {
		console.warn('Error submitting bug report', error);
		if (axios.isAxiosError(error) && error.response?.status === 401) return [null, BUG_TRACKER_AUTH_EXPIRED];
		return [null, 'Failed to submit bug report'];
	}
};
